import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Settings, Users, Building2, Palette, FileText, Shield,
  Plus, Trash2, Save, Download, Target, Zap,
  AlertCircle, CheckCircle2, Sliders, RefreshCw, Mail, Send, UserPlus,
  Info, Wifi, XCircle, AlertTriangle, ToggleLeft, ToggleRight, FileDown, Database,
  Calendar, Link2, Activity, Clock, Edit2, X
} from 'lucide-react';
import { toast } from 'sonner';
import { MainLayout } from '../components/MainLayout';
import { InviteManagement } from './InviteManagement';
import { DataProtectionPanel } from './DataProtectionPanel';
import { DatabaseManagement } from './DatabaseManagement';

// Role labels for display
const ROLE_LABELS = {
  director: 'Director',
  bd_manager: 'Business Development Manager',
  senior_engineer: 'Senior Engineer',
  associate_engineer: 'Associate Engineer',
  engineer: 'Engineer'
};

export const AdminPage = () => {
  const { user, authFetch } = useAuth();
  const [activeTab, setActiveTab] = useState('team');
  const [loading, setLoading] = useState(true);
  
  // Team data
  const [team, setTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Branding
  const [branding, setBranding] = useState({
    company_name: '',
    logo_url: '',
    logo_data: '',
    primary_color: '#1e3a5f',
    secondary_color: '#64748b',
    email_signature: '',
    quote_header: '',
    quote_footer: '',
  });
  
  // Email Digest - Enhanced with scheduling
  const [emailDigest, setEmailDigest] = useState({
    enabled: false,
    day_of_week: 'friday',
    hour: 16,
    minute: 30,
    recipients: [],
  });
  const [digestLoading, setDigestLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ configured: false, error: null });
  const [nextDigestRun, setNextDigestRun] = useState(null);
  const [eligibleRecipients, setEligibleRecipients] = useState([]);
  const [digestHistory, setDigestHistory] = useState([]);
  const [showDigestPreview, setShowDigestPreview] = useState(false);
  const [digestPreviewHtml, setDigestPreviewHtml] = useState('');
  
  // Decision Rules
  const [decisionSettings, setDecisionSettings] = useState({
    min_construction_value: 100000,
    min_fee_value: 10000,
    high_risk_blocks_quote: true,
  });
  
  // Residential Enquiry Wizard Thresholds
  const [resThresholds, setResThresholds] = useState({
    scale_threshold: 500000,
    fee_threshold: 50000,
    min_criteria_pass: 3,
  });
  const [resThresholdsSaving, setResThresholdsSaving] = useState(false);
  
  // Routines
  const [routines, setRoutines] = useState([]);
  // Routine modal state - modal UI not yet implemented
  // eslint-disable-next-line no-unused-vars
  const [showRoutineModal, setShowRoutineModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [editRoutine, setEditRoutine] = useState(null);
  
  // Invite Modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'engineer'
  });

  // Edit Team Member Modal
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [editMemberData, setEditMemberData] = useState(null);
  const [editMemberSaving, setEditMemberSaving] = useState(false);

  // SMTP Configuration (Director only)
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    smtp_use_ssl: false,
    sender_email: '',
    sender_name: 'FURD OpsFlow',
    reply_to_email: ''
  });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestPassed, setSmtpTestPassed] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');

  // Build Info (Director only)
  const [buildInfo, setBuildInfo] = useState(null);

  // Feature Flags (Director only)
  const [featureFlags, setFeatureFlags] = useState({});
  const [featureFlagsLoading, setFeatureFlagsLoading] = useState(false);

  // Data Export
  const [exportLoading, setExportLoading] = useState(null);

  // Calendar Subscriptions (Director only)
  const [calendarSubscriptions, setCalendarSubscriptions] = useState([]);
  const [subscriptionSummary, setSubscriptionSummary] = useState(null);
  const [showRevokedSubscriptions, setShowRevokedSubscriptions] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [calendarSettings, setCalendarSettings] = useState(null);
  const [calendarHealth, setCalendarHealth] = useState(null);
  const [calendarHealthLoading, setCalendarHealthLoading] = useState(false);

  // Company Strengths (Director only)
  const [companyStrengths, setCompanyStrengths] = useState([]);
  const [strengthsLoading, setStrengthsLoading] = useState(false);
  const [newStrength, setNewStrength] = useState({ name: '', description: '' });
  const [showAddStrength, setShowAddStrength] = useState(false);

  const loadData = useCallback(async () => {
    try {
      // Load team
      const teamRes = await authFetch('/admin/team');
      if (teamRes.ok) {
        const data = await teamRes.json();
        setTeam(data.team);
        setTeamMembers(data.members || []);
      }
      
      // Load branding
      const brandRes = await authFetch('/admin/branding');
      if (brandRes.ok) {
        const data = await brandRes.json();
        if (data.branding) setBranding(data.branding);
      }
      
      // Load decision settings
      const settingsRes = await authFetch('/admin/decision-settings');
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        if (data.settings) setDecisionSettings(data.settings);
      }
      
      // Load routines
      const routinesRes = await authFetch('/admin/routines');
      if (routinesRes.ok) {
        const data = await routinesRes.json();
        setRoutines(data.routines || []);
      }
      
      // Load email digest config with enhanced data
      const digestRes = await authFetch('/admin/email-digest');
      if (digestRes.ok) {
        const data = await digestRes.json();
        if (data.config) {
          setEmailDigest({
            enabled: data.config.enabled || false,
            day_of_week: data.config.day_of_week || 'friday',
            hour: data.config.hour ?? 16,
            minute: data.config.minute ?? 30,
            recipients: data.config.recipients || [],
          });
        }
        if (data.next_run) setNextDigestRun(data.next_run);
        if (data.email_status) setEmailStatus(data.email_status);
      }
      
      // Load eligible recipients
      const recipientsRes = await authFetch('/admin/email-digest/recipients');
      if (recipientsRes.ok) {
        const data = await recipientsRes.json();
        setEligibleRecipients(data.recipients || []);
      }
      
      // Load digest history
      const historyRes = await authFetch('/admin/email-digest/history?limit=10');
      if (historyRes.ok) {
        const data = await historyRes.json();
        setDigestHistory(data.history || []);
      }

      // Load SMTP config (Director only)
      const smtpRes = await authFetch('/admin/smtp-config');
      if (smtpRes.ok) {
        const data = await smtpRes.json();
        if (data.config) {
          setSmtpConfig({
            smtp_host: data.config.smtp_host || '',
            smtp_port: data.config.smtp_port || 587,
            smtp_username: data.config.smtp_username || '',
            smtp_password: '', // Never returned, always blank
            smtp_use_tls: data.config.smtp_use_tls ?? true,
            smtp_use_ssl: data.config.smtp_use_ssl ?? false,
            sender_email: data.config.sender_email || '',
            sender_name: data.config.sender_name || 'FURD OpsFlow',
            reply_to_email: data.config.reply_to_email || ''
          });
          setSmtpConfigured(data.config.configured || false);
          setSmtpTestPassed(data.config.test_passed || false);
        }
      }

      // Load Build Info (Director only) - includes DB and scheduler status
      const buildRes = await authFetch('/system/build-info');
      if (buildRes.ok) {
        const data = await buildRes.json();
        setBuildInfo(data);
      } else {
        // Fallback to old endpoint if new one doesn't exist
        const oldBuildRes = await authFetch('/admin/build-info');
        if (oldBuildRes.ok) {
          const data = await oldBuildRes.json();
          setBuildInfo(data);
        }
      }

      // Load Feature Flags (Director only)
      const flagsRes = await authFetch('/admin/feature-flags');
      if (flagsRes.ok) {
        const data = await flagsRes.json();
        setFeatureFlags(data.flags || {});
      }

      // Load Calendar Subscriptions with summary (Director only)
      const calSubsRes = await authFetch('/calendar/subscriptions?include_revoked=true');
      if (calSubsRes.ok) {
        const data = await calSubsRes.json();
        setCalendarSubscriptions(data.subscriptions || []);
        setSubscriptionSummary(data.summary || null);
      }

      // Load Calendar Settings
      const calSettingsRes = await authFetch('/calendar/settings');
      if (calSettingsRes.ok) {
        const data = await calSettingsRes.json();
        setCalendarSettings(data.settings);
      }

      // Load Calendar Health (Director only)
      const calHealthRes = await authFetch('/calendar/health');
      if (calHealthRes.ok) {
        const data = await calHealthRes.json();
        setCalendarHealth(data);
      }

      // Load Company Strengths
      const strengthsRes = await authFetch('/admin/company-strengths');
      if (strengthsRes.ok) {
        const data = await strengthsRes.json();
        setCompanyStrengths(data.strengths || []);
      }

      // Load Residential Wizard Thresholds
      const resThreshRes = await authFetch('/admin/residential-thresholds');
      if (resThreshRes.ok) {
        const data = await resThreshRes.json();
        if (data.thresholds) setResThresholds(data.thresholds);
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (user?.role !== 'director') return;
    loadData();
  }, [user, loadData]);

  const saveBranding = async () => {
    try {
      const res = await authFetch('/admin/branding', {
        method: 'PUT',
        body: branding
      });
      if (res.ok) {
        toast.success('Branding saved');
      }
    } catch (err) {
      toast.error('Failed to save branding');
    }
  };

  const saveEmailDigest = async () => {
    setDigestLoading(true);
    try {
      const res = await authFetch('/admin/email-digest', {
        method: 'PUT',
        body: {
          enabled: emailDigest.enabled,
          day_of_week: emailDigest.day_of_week,
          hour: emailDigest.hour,
          minute: emailDigest.minute,
          recipients: emailDigest.recipients
        }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Email digest settings saved');
        if (data.next_run) setNextDigestRun(data.next_run);
        // Reload history
        const historyRes = await authFetch('/admin/email-digest/history?limit=10');
        if (historyRes.ok) {
          const hdata = await historyRes.json();
          setDigestHistory(hdata.history || []);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save email digest settings');
      }
    } catch (err) {
      toast.error('Failed to save email digest settings');
    } finally {
      setDigestLoading(false);
    }
  };

  const sendDigestNow = async () => {
    setDigestLoading(true);
    try {
      const res = await authFetch('/admin/email-digest/send-now', {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          toast.success(`Digest sent to ${data.sent_count} recipients!`);
        } else {
          toast.error(data.error || 'Failed to send digest');
        }
        // Reload history
        const historyRes = await authFetch('/admin/email-digest/history?limit=10');
        if (historyRes.ok) {
          const hdata = await historyRes.json();
          setDigestHistory(hdata.history || []);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send digest');
      }
    } catch (err) {
      toast.error('Failed to send digest');
    } finally {
      setDigestLoading(false);
    }
  };

  const sendTestDigest = async () => {
    setDigestLoading(true);
    try {
      const res = await authFetch('/admin/email-digest/send-test', {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          toast.success(`Test digest sent to ${data.sent_count} recipients!`);
        } else {
          toast.error(data.error || 'Failed to send test digest');
        }
        // Reload history
        const historyRes = await authFetch('/admin/email-digest/history?limit=10');
        if (historyRes.ok) {
          const hdata = await historyRes.json();
          setDigestHistory(hdata.history || []);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send test digest');
      }
    } catch (err) {
      toast.error('Failed to send test digest');
    } finally {
      setDigestLoading(false);
    }
  };

  const loadDigestPreview = async () => {
    try {
      const res = await authFetch('/admin/email-digest/preview?format=json');
      if (res.ok) {
        const data = await res.json();
        setDigestPreviewHtml(data.html);
        setShowDigestPreview(true);
      }
    } catch (err) {
      toast.error('Failed to load preview');
    }
  };

  const toggleRecipient = (userId) => {
    setEmailDigest(prev => {
      const recipients = prev.recipients || [];
      if (recipients.includes(userId)) {
        return { ...prev, recipients: recipients.filter(id => id !== userId) };
      } else {
        return { ...prev, recipients: [...recipients, userId] };
      }
    });
  };

  // SMTP Configuration functions
  const saveSmtpConfig = async () => {
    try {
      const payload = { 
        smtp_host: smtpConfig.smtp_host,
        smtp_port: smtpConfig.smtp_port,
        smtp_username: smtpConfig.smtp_username,
        smtp_use_tls: smtpConfig.smtp_use_tls,
        smtp_use_ssl: smtpConfig.smtp_use_ssl,
        sender_email: smtpConfig.sender_email,
        sender_name: smtpConfig.sender_name,
        reply_to_email: smtpConfig.reply_to_email || null
      };
      // Only include password if user entered a new one
      if (smtpConfig.smtp_password) {
        payload.smtp_password = smtpConfig.smtp_password;
      }
      
      const res = await authFetch('/admin/smtp-config', {
        method: 'PUT',
        body: payload
      });
      if (res.ok) {
        toast.success('SMTP configuration saved');
        setSmtpConfig(prev => ({ ...prev, smtp_password: '' })); // Clear password field
        // Reload to get updated status
        const smtpRes = await authFetch('/admin/smtp-config');
        if (smtpRes.ok) {
          const data = await smtpRes.json();
          setSmtpConfigured(data.config?.configured || false);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save SMTP configuration');
      }
    } catch (err) {
      toast.error('Failed to save SMTP configuration');
    }
  };

  const testSmtpConnection = async () => {
    setSmtpTesting(true);
    setSmtpTestResult(null);
    try {
      const res = await authFetch('/admin/smtp-config/test', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success('SMTP connection successful!');
      } else {
        toast.error(data.error || 'SMTP connection failed');
      }
    } catch (err) {
      toast.error('Failed to test SMTP connection');
    } finally {
      setSmtpTesting(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSmtpTesting(true);
    setSmtpTestResult(null);
    
    try {
      const res = await authFetch('/admin/smtp-config/send-test', {
        method: 'POST',
        body: { to_email: testEmailAddress }
      });
      const data = await res.json();
      
      if (data.success) {
        setSmtpTestResult({ success: true });
        setSmtpTestPassed(true);
        toast.success('Test email sent successfully! Check your inbox.');
      } else {
        setSmtpTestResult({ success: false, error: data.error || 'Failed to send test email' });
        toast.error(data.error || 'Failed to send test email');
      }
    } catch (err) {
      setSmtpTestResult({ success: false, error: 'Network error - could not send test email' });
      toast.error('Failed to send test email');
    } finally {
      setSmtpTesting(false);
    }
  };

  const saveDecisionSettings = async () => {
    try {
      const res = await authFetch('/admin/decision-settings', {
        method: 'PUT',
        body: decisionSettings
      });
      if (res.ok) {
        toast.success('Decision rules saved');
      }
    } catch (err) {
      toast.error('Failed to save decision rules');
    }
  };

  const toggleFeatureFlag = async (flagKey, currentValue) => {
    setFeatureFlagsLoading(true);
    try {
      const res = await authFetch(`/admin/feature-flags/${flagKey}`, {
        method: 'PUT',
        body: { enabled: !currentValue }
      });
      if (res.ok) {
        setFeatureFlags(prev => ({
          ...prev,
          [flagKey]: { ...prev[flagKey], enabled: !currentValue }
        }));
        toast.success(`Feature ${!currentValue ? 'enabled' : 'disabled'}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update feature flag');
      }
    } catch (err) {
      toast.error('Failed to update feature flag');
    } finally {
      setFeatureFlagsLoading(false);
    }
  };

  // Company Strengths functions
  const saveCompanyStrengths = async () => {
    setStrengthsLoading(true);
    try {
      const res = await authFetch('/admin/company-strengths', {
        method: 'PUT',
        body: { strengths: companyStrengths }
      });
      if (res.ok) {
        toast.success('Company strengths saved');
      } else {
        toast.error('Failed to save strengths');
      }
    } catch (err) {
      toast.error('Failed to save strengths');
    } finally {
      setStrengthsLoading(false);
    }
  };

  const toggleStrength = (strengthId) => {
    setCompanyStrengths(prev => prev.map(s => 
      s.id === strengthId ? { ...s, enabled: !s.enabled } : s
    ));
  };

  const addCustomStrength = async () => {
    if (!newStrength.name.trim()) {
      toast.error('Please enter a strength name');
      return;
    }
    
    try {
      const res = await authFetch('/admin/company-strengths/add', {
        method: 'POST',
        body: {
          id: `custom_${Date.now()}`,
          name: newStrength.name,
          description: newStrength.description,
          enabled: true,
          is_default: false
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setCompanyStrengths(prev => [...prev, data.strength]);
        setNewStrength({ name: '', description: '' });
        setShowAddStrength(false);
        toast.success('Custom strength added');
      }
    } catch (err) {
      toast.error('Failed to add strength');
    }
  };

  const removeCustomStrength = (strengthId) => {
    setCompanyStrengths(prev => prev.filter(s => s.id !== strengthId));
  };

  const exportAllData = async (_format = 'json') => {
    setExportLoading('all');
    try {
      const res = await authFetch('/admin/export-data');
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `opsflow-full-export-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Full data export downloaded');
      } else {
        toast.error('Export failed');
      }
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExportLoading(null);
    }
  };

  const exportDataCsv = async (dataType) => {
    setExportLoading(dataType);
    try {
      const res = await authFetch(`/admin/export-data/csv?data_type=${dataType}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `opsflow-${dataType}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${dataType} exported as CSV`);
      } else {
        toast.error('CSV export failed');
      }
    } catch (err) {
      toast.error('CSV export failed');
    } finally {
      setExportLoading(null);
    }
  };

  const refreshCalendarHealth = async () => {
    setCalendarHealthLoading(true);
    try {
      const res = await authFetch('/calendar/health');
      if (res.ok) {
        const data = await res.json();
        setCalendarHealth(data);
        toast.success('Calendar health refreshed');
      } else {
        toast.error('Failed to refresh calendar health');
      }
    } catch (err) {
      toast.error('Failed to refresh calendar health');
    } finally {
      setCalendarHealthLoading(false);
    }
  };

  const exportData = async (type) => {
    try {
      const res = await authFetch(`/admin/export/${type}`);
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `opsflow-${type}-export.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${type} exported`);
      }
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const inviteTeamMember = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch('/users', {
        method: 'POST',
        body: {
          email: inviteData.email,
          first_name: inviteData.first_name,
          last_name: inviteData.last_name,
          role: inviteData.role
        }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(
          <div>
            <p className="font-medium">User created successfully!</p>
            {data.temporary_password && (
              <p className="text-sm mt-1">
                Temporary password: <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">{data.temporary_password}</span>
              </p>
            )}
            <p className="text-xs text-slate-500 mt-1">Share these credentials securely with the user.</p>
          </div>,
          { duration: 15000 }
        );
        setShowInviteModal(false);
        setInviteData({ email: '', first_name: '', last_name: '', role: 'engineer' });
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create user');
      }
    } catch (err) {
      toast.error('Failed to create team member');
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm('Are you sure you want to deactivate this user?')) return;
    try {
      const res = await authFetch(`/users/${userId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('User deactivated');
        loadData();
      }
    } catch (err) {
      toast.error('Failed to deactivate user');
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const res = await authFetch(`/users/${userId}`, {
        method: 'PUT',
        body: { role: newRole }
      });
      if (res.ok) {
        toast.success('Role updated');
        loadData();
      }
    } catch (err) {
      toast.error('Failed to update role');
    }
  };

  const resetUserPassword = async (userId) => {
    if (!window.confirm('Reset password for this user?')) return;
    try {
      const res = await authFetch(`/users/${userId}/reset-password`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(
          <div>
            <p className="font-medium">Password reset!</p>
            <p className="text-sm mt-1">
              New password: <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">{data.temporary_password}</span>
            </p>
          </div>,
          { duration: 15000 }
        );
      } else {
        toast.error('Failed to reset password');
      }
    } catch (err) {
      toast.error('Failed to reset password');
    }
  };

  const _deactivateUser = async (userId) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      const res = await authFetch(`/admin/users/${userId}/deactivate`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('User deactivated');
        loadData();
      }
    } catch (err) {
      toast.error('Failed to deactivate user');
    }
  };

  // Update team member profile (name)
  const handleEditMember = (member) => {
    setEditMemberData({
      id: member.id,
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      email: member.email
    });
    setShowEditMemberModal(true);
  };

  const saveEditMember = async () => {
    if (!editMemberData) return;
    
    if (!editMemberData.first_name?.trim() || !editMemberData.last_name?.trim()) {
      toast.error('First and last name are required');
      return;
    }

    setEditMemberSaving(true);
    try {
      const res = await authFetch(`/admin/team/members/${editMemberData.id}/profile`, {
        method: 'PUT',
        body: {
          first_name: editMemberData.first_name.trim(),
          last_name: editMemberData.last_name.trim()
        }
      });

      if (res.ok) {
        toast.success('Team member profile updated');
        setShowEditMemberModal(false);
        setEditMemberData(null);
        loadData(); // Reload to show updated info
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update profile');
      }
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setEditMemberSaving(false);
    }
  };

  if (user?.role !== 'director') {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-semibold text-slate-700 mb-2">
              Director Access Only
            </h3>
            <p className="text-slate-500">
              You need Director privileges to access admin settings
            </p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="admin-page">
        {/* Header */}
        <div className="max-w-full">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1e3a5f]">
            Admin Settings
          </h1>
          <p className="text-slate-500 mt-1">
            Configure team, branding, decision rules, and routines
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 md:space-y-6 w-full">
          {/* Mobile: Dropdown selector for tabs - Always visible on mobile */}
          <div className="md:hidden">
            <label className="block text-sm font-medium text-slate-600 mb-2">Section</label>
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full bg-white border-slate-200 h-12 text-sm">
                <SelectValue placeholder="Select section" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="team">
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" /> Team Information
                  </span>
                </SelectItem>
                <SelectItem value="invites">
                  <span className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4" /> Invites
                  </span>
                </SelectItem>
                <SelectItem value="database">
                  <span className="flex items-center gap-2">
                    <Settings className="w-4 h-4" /> Database
                  </span>
                </SelectItem>
                <SelectItem value="branding">
                  <span className="flex items-center gap-2">
                    <Palette className="w-4 h-4" /> Branding
                  </span>
                </SelectItem>
                <SelectItem value="decision-rules">
                  <span className="flex items-center gap-2">
                    <Sliders className="w-4 h-4" /> Decision Rules
                  </span>
                </SelectItem>
                <SelectItem value="routines">
                  <span className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4" /> Routines
                  </span>
                </SelectItem>
                <SelectItem value="data-protection">
                  <span className="flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Data Protection
                  </span>
                </SelectItem>
                <SelectItem value="feature-flags">
                  <span className="flex items-center gap-2">
                    <ToggleLeft className="w-4 h-4" /> Feature Flags
                  </span>
                </SelectItem>
                <SelectItem value="email-digest">
                  <span className="flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email Digest
                  </span>
                </SelectItem>
                <SelectItem value="export">
                  <span className="flex items-center gap-2">
                    <Download className="w-4 h-4" /> Export Data
                  </span>
                </SelectItem>
                <SelectItem value="calendar">
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Calendar
                  </span>
                </SelectItem>
                <SelectItem value="system">
                  <span className="flex items-center gap-2">
                    <Settings className="w-4 h-4" /> System / SMTP
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Desktop: Tab list that wraps to multiple rows on smaller screens */}
          <div className="hidden md:block admin-tabs-container">
            <TabsList className="bg-slate-100 p-1 rounded-xl admin-tabs-list">
              <TabsTrigger value="team" className="rounded-lg text-xs lg:text-sm whitespace-nowrap">
                <Users className="w-4 h-4 mr-1 lg:mr-2" />
                Team
              </TabsTrigger>
              <TabsTrigger value="invites" className="rounded-lg text-xs lg:text-sm whitespace-nowrap">
                <UserPlus className="w-4 h-4 mr-1 lg:mr-2" />
                Invites
              </TabsTrigger>
              <TabsTrigger value="database" className="rounded-lg text-xs lg:text-sm whitespace-nowrap">
                <Settings className="w-4 h-4 mr-1 lg:mr-2" />
                Database
              </TabsTrigger>
              <TabsTrigger value="branding" className="rounded-lg text-xs lg:text-sm whitespace-nowrap">
                <Palette className="w-4 h-4 mr-1 lg:mr-2" />
                Branding
              </TabsTrigger>
              <TabsTrigger value="decision-rules" className="rounded-lg text-xs lg:text-sm whitespace-nowrap">
                <Sliders className="w-4 h-4 mr-1 lg:mr-2" />
                Rules
              </TabsTrigger>
              <TabsTrigger value="strengths" className="rounded-lg text-xs lg:text-sm whitespace-nowrap">
                <Target className="w-4 h-4 mr-1 lg:mr-2" />
                Strengths
              </TabsTrigger>
              <TabsTrigger value="routines" className="rounded-lg text-xs lg:text-sm whitespace-nowrap">
                <RefreshCw className="w-4 h-4 mr-1 lg:mr-2" />
                Routines
              </TabsTrigger>
              <TabsTrigger value="data-protection" className="rounded-lg text-xs lg:text-sm whitespace-nowrap">
                <Shield className="w-4 h-4 mr-1 lg:mr-2" />
                GDPR
              </TabsTrigger>
              <TabsTrigger value="feature-flags" className="rounded-lg text-xs lg:text-sm whitespace-nowrap">
                <ToggleLeft className="w-4 h-4 mr-1 lg:mr-2" />
                Flags
              </TabsTrigger>
              <TabsTrigger value="email-digest" className="rounded-lg text-xs lg:text-sm whitespace-nowrap">
                <Mail className="w-4 h-4 mr-1 lg:mr-2" />
                Digest
              </TabsTrigger>
              <TabsTrigger value="export" className="rounded-lg text-xs lg:text-sm whitespace-nowrap">
                <Download className="w-4 h-4 mr-1 lg:mr-2" />
                Export
              </TabsTrigger>
              <TabsTrigger value="calendar" className="rounded-lg text-xs lg:text-sm whitespace-nowrap">
                <Calendar className="w-4 h-4 mr-1 lg:mr-2" />
                Calendar
              </TabsTrigger>
              <TabsTrigger value="system" className="rounded-lg text-xs lg:text-sm whitespace-nowrap">
                <Settings className="w-4 h-4 mr-1 lg:mr-2" />
                System
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-4 md:space-y-6 max-w-full overflow-x-hidden">
            {/* Team Info */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6 max-w-full overflow-hidden">
              <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4 text-base md:text-lg">
                Team Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-500 text-sm">Team Name</Label>
                  <div className="flex gap-2">
                    <Input
                      value={team?.name || ''}
                      onChange={(e) => setTeam(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter team name"
                      className="flex-1"
                      data-testid="team-name-input"
                    />
                    <Button
                      onClick={async () => {
                        if (!team?.name || team.name.trim().length < 2) {
                          toast.error('Team name must be at least 2 characters');
                          return;
                        }
                        try {
                          const res = await authFetch('/admin/team/name', {
                            method: 'PUT',
                            body: { name: team.name.trim() }
                          });
                          if (res.ok) {
                            toast.success('Team name updated!');
                          } else {
                            const err = await res.json();
                            toast.error(err.detail || 'Failed to update team name');
                          }
                        } catch (err) {
                          toast.error('Failed to update team name');
                        }
                      }}
                      className="bg-[#1e3a5f] hover:bg-[#162c46] text-white"
                      size="sm"
                    >
                      <Save className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-500 text-sm">Team Code</Label>
                  <p className="font-mono text-slate-800 mt-2">{team?.code || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Team Members */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-4 md:px-6 py-3 md:py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-heading font-semibold text-[#1e3a5f] text-base md:text-lg">
                  Team Members ({teamMembers.length})
                </h3>
                <Button
                  onClick={() => setShowInviteModal(true)}
                  className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full text-xs md:text-sm px-3 md:px-4 h-8 md:h-9"
                >
                  <Plus className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Invite Member</span>
                  <span className="md:hidden">Invite</span>
                </Button>
              </div>
              <div className="divide-y divide-slate-100">
                {teamMembers.map(member => (
                  <div key={member.id} className="px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">
                        {member.first_name} {member.last_name}
                      </p>
                      <p className="text-sm text-slate-500 truncate">{member.email}</p>
                      {member.is_invited && member.must_change_password && (
                        <span className="text-xs text-amber-600">Pending first login</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select
                        value={member.role}
                        onValueChange={(value) => updateUserRole(member.id, value)}
                        disabled={member.id === user.id}
                      >
                        <SelectTrigger className="w-28 md:w-36 text-xs md:text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="director">Director</SelectItem>
                          <SelectItem value="bd_manager">BD Manager</SelectItem>
                          <SelectItem value="senior_engineer">Senior Engineer</SelectItem>
                          <SelectItem value="associate_engineer">Associate Engineer</SelectItem>
                          <SelectItem value="engineer">Engineer</SelectItem>
                        </SelectContent>
                      </Select>
                      {member.id !== user.id && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditMember(member)}
                            className="text-slate-500 hover:bg-slate-100 px-2"
                            title="Edit Name"
                            data-testid={`edit-member-${member.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resetUserPassword(member.id)}
                            className="text-slate-500 hover:bg-slate-100 px-2"
                            title="Reset Password"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteUser(member.id)}
                            className="text-red-600 hover:bg-red-50 px-2"
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Invites Tab */}
          <TabsContent value="invites">
            <InviteManagement />
          </TabsContent>

          {/* Database Tab */}
          <TabsContent value="database">
            <DatabaseManagement authFetch={authFetch} userRole={user?.role} />
          </TabsContent>

          {/* Data Protection Tab */}
          <TabsContent value="data-protection">
            <DataProtectionPanel />
          </TabsContent>

          {/* Feature Flags Tab */}
          <TabsContent value="feature-flags" className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading font-semibold text-[#1e3a5f]">
                    Feature Flags
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Control which features are enabled for your team
                  </p>
                </div>
                <Badge variant="outline" className="text-slate-500">
                  Director Only
                </Badge>
              </div>

              {Object.keys(featureFlags).length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <ToggleLeft className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No feature flags configured</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(featureFlags).map(([key, flag]) => (
                    <div 
                      key={key}
                      className={`p-4 rounded-xl border transition-all ${
                        flag.enabled 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-800">{flag.name || key}</p>
                            {flag.director_only && (
                              <Badge variant="outline" className="text-xs">Director Only</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{flag.description || 'No description'}</p>
                        </div>
                        <button
                          onClick={() => toggleFeatureFlag(key, flag.enabled)}
                          disabled={featureFlagsLoading}
                          className={`w-14 h-8 rounded-full transition-all flex-shrink-0 ${
                            flag.enabled ? 'bg-emerald-500' : 'bg-slate-300'
                          } ${featureFlagsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <div className={`w-6 h-6 bg-white rounded-full shadow transition-transform ${
                            flag.enabled ? 'translate-x-7' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-600">
                    <p className="font-medium mb-1">About Feature Flags</p>
                    <p>Feature flags allow you to enable or disable specific functionality for your team. Some features may incur additional costs or require specific configurations when enabled.</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding" className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <h3 className="font-heading font-semibold text-[#1e3a5f] mb-6">
                Company Branding
              </h3>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      value={branding.company_name}
                      onChange={(e) => setBranding(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Your Company Ltd"
                      data-testid="branding-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Logo</Label>
                    <div className="flex items-center gap-4">
                      {branding.logo_data || branding.logo_url ? (
                        <img 
                          src={branding.logo_data || branding.logo_url} 
                          alt="Logo" 
                          className="h-12 w-auto object-contain bg-slate-100 rounded-lg p-2"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="h-12 w-24 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 text-xs">
                          No logo
                        </div>
                      )}
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/png,image/svg+xml,image/jpeg,image/jpg"
                          className="hidden"
                          data-testid="logo-upload-input"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            // Validate file size
                            if (file.size > 2 * 1024 * 1024) {
                              toast.error('File too large. Max 2MB.');
                              return;
                            }
                            
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            try {
                              toast.loading('Uploading logo...');
                              const res = await fetch(
                                `${process.env.REACT_APP_BACKEND_URL}/api/admin/logo/upload`,
                                {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('opsflow_token')}`
                                  },
                                  body: formData
                                }
                              );
                              
                              toast.dismiss();
                              
                              if (res.ok) {
                                const data = await res.json();
                                setBranding(prev => ({ ...prev, logo_data: data.logo_url }));
                                toast.success('Logo uploaded successfully!');
                                // Reload page to show new logo in sidebar
                                setTimeout(() => window.location.reload(), 1000);
                              } else {
                                const err = await res.json();
                                toast.error(err.detail || 'Failed to upload logo');
                              }
                            } catch (err) {
                              toast.dismiss();
                              console.error('Upload error:', err);
                              toast.error('Failed to upload logo. Please try again.');
                            }
                          }}
                        />
                        <span className="px-4 py-2 bg-[#1e3a5f] hover:bg-[#162c46] rounded-lg text-sm font-medium text-white transition-colors cursor-pointer inline-flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          Upload Logo
                        </span>
                      </label>
                      {branding.logo_data && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setBranding(prev => ({ ...prev, logo_data: '', logo_url: '' }))}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">PNG, SVG, or JPEG. Max 2MB.</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={branding.primary_color}
                        onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={branding.primary_color}
                        onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary Color</Label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={branding.secondary_color}
                        onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={branding.secondary_color}
                        onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                        className="font-mono"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Email Signature</Label>
                  <Textarea
                    value={branding.email_signature}
                    onChange={(e) => setBranding(prev => ({ ...prev, email_signature: e.target.value }))}
                    placeholder="Default email signature for scripts..."
                    className="min-h-[100px]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Quote Header</Label>
                  <Textarea
                    value={branding.quote_header}
                    onChange={(e) => setBranding(prev => ({ ...prev, quote_header: e.target.value }))}
                    placeholder="Text to appear at top of quotes..."
                    className="min-h-[80px]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Quote Footer / Terms</Label>
                  <Textarea
                    value={branding.quote_footer}
                    onChange={(e) => setBranding(prev => ({ ...prev, quote_footer: e.target.value }))}
                    placeholder="Default terms and conditions..."
                    className="min-h-[100px]"
                  />
                </div>
                
                <Button
                  onClick={saveBranding}
                  className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full px-6"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Branding
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Decision Rules Tab */}
          <TabsContent value="decision-rules" className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <h3 className="font-heading font-semibold text-[#1e3a5f] mb-6">
                Decision Engine Settings
              </h3>
              <p className="text-sm text-slate-500 mb-6">
                These values feed into the Quote/Don't-Quote decision engine when evaluating new enquiries.
              </p>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min Construction Value (£)</Label>
                    <Input
                      type="number"
                      value={decisionSettings.min_construction_value}
                      onChange={(e) => setDecisionSettings(prev => ({
                        ...prev,
                        min_construction_value: parseInt(e.target.value) || 0
                      }))}
                      data-testid="min-construction-value"
                    />
                    <p className="text-xs text-slate-500">Projects below this value may trigger "Don't Quote"</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Min Fee Value (£)</Label>
                    <Input
                      type="number"
                      value={decisionSettings.min_fee_value}
                      onChange={(e) => setDecisionSettings(prev => ({
                        ...prev,
                        min_fee_value: parseInt(e.target.value) || 0
                      }))}
                    />
                    <p className="text-xs text-slate-500">Minimum fee threshold for the decision engine</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                  <Checkbox
                    checked={decisionSettings.high_risk_blocks_quote}
                    onCheckedChange={(checked) => setDecisionSettings(prev => ({
                      ...prev,
                      high_risk_blocks_quote: checked
                    }))}
                  />
                  <div>
                    <Label className="cursor-pointer font-medium">
                      HIGH risk in any category blocks quote creation
                    </Label>
                    <p className="text-xs text-slate-500">When enabled, any HIGH risk flag in the decision engine prevents creating a quote (Director can still override)</p>
                  </div>
                </div>
                
                <div className="border-t border-slate-100 pt-6">
                  <h4 className="font-medium text-slate-700 mb-2">Residential Enquiry Thresholds</h4>
                  <p className="text-sm text-slate-500 mb-4">Minimum requirements for the Residential Sub Wizard when creating new enquiries.</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Min Construction Value (£)</Label>
                      <Input
                        type="number"
                        value={resThresholds.scale_threshold}
                        onChange={(e) => setResThresholds(prev => ({ ...prev, scale_threshold: parseInt(e.target.value) || 0 }))}
                        data-testid="res-scale-threshold"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Min Fee Value (£)</Label>
                      <Input
                        type="number"
                        value={resThresholds.fee_threshold}
                        onChange={(e) => setResThresholds(prev => ({ ...prev, fee_threshold: parseInt(e.target.value) || 0 }))}
                        data-testid="res-fee-threshold"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Min Criteria to Pass</Label>
                      <Input
                        type="number"
                        min={1}
                        max={5}
                        value={resThresholds.min_criteria_pass}
                        onChange={(e) => setResThresholds(prev => ({ ...prev, min_criteria_pass: parseInt(e.target.value) || 3 }))}
                        data-testid="res-min-criteria"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={async () => {
                      setResThresholdsSaving(true);
                      try {
                        const res = await authFetch('/admin/residential-thresholds', {
                          method: 'PUT',
                          body: resThresholds
                        });
                        if (res.ok) toast.success('Residential thresholds saved');
                        else toast.error('Failed to save');
                      } catch { toast.error('Failed to save'); }
                      finally { setResThresholdsSaving(false); }
                    }}
                    disabled={resThresholdsSaving}
                    className="mt-4 bg-[#1e3a5f] hover:bg-[#162c46] text-white"
                    size="sm"
                    data-testid="save-res-thresholds"
                  >
                    {resThresholdsSaving ? 'Saving...' : 'Save Residential Thresholds'}
                  </Button>
                </div>
                
                <Button
                  onClick={saveDecisionSettings}
                  className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full px-6"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Decision Rules
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Company Strengths Tab */}
          <TabsContent value="strengths" className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading font-semibold text-[#1e3a5f] flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Company MEP Strengths
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Define your company&apos;s core MEP expertise areas. These are used to assess &ldquo;Alignment with Strengths&rdquo; for residential projects.
                  </p>
                </div>
                <Button
                  onClick={() => setShowAddStrength(true)}
                  className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Custom
                </Button>
              </div>
              
              {/* Strengths Grid */}
              <div className="grid gap-3">
                {companyStrengths.map(strength => (
                  <div
                    key={strength.id}
                    className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      strength.enabled 
                        ? 'border-[#1e3a5f] bg-[#1e3a5f]/5' 
                        : 'border-slate-200 bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div 
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                          strength.enabled 
                            ? 'bg-[#1e3a5f] text-white' 
                            : 'bg-slate-200 text-slate-500'
                        }`}
                      >
                        <Zap className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium ${strength.enabled ? 'text-[#1e3a5f]' : 'text-slate-600'}`}>
                          {strength.name}
                        </p>
                        <p className="text-sm text-slate-500 truncate">
                          {strength.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleStrength(strength.id)}
                        className={`rounded-full px-4 ${
                          strength.enabled 
                            ? 'text-[#1e3a5f] hover:bg-[#1e3a5f]/10' 
                            : 'text-slate-500 hover:bg-slate-100'
                        }`}
                      >
                        {strength.enabled ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 mr-1" />
                            Inactive
                          </>
                        )}
                      </Button>
                      {!strength.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCustomStrength(strength.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Add Custom Strength Form */}
              {showAddStrength && (
                <div className="mt-4 p-4 rounded-xl border border-slate-200 bg-slate-50">
                  <h4 className="font-medium text-slate-700 mb-3">Add Custom Strength</h4>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-slate-600 text-sm">Strength Name</Label>
                      <Input
                        value={newStrength.name}
                        onChange={(e) => setNewStrength(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., Industrial Warehouses"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Description</Label>
                      <Input
                        value={newStrength.description}
                        onChange={(e) => setNewStrength(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="e.g., Large-scale industrial MEP systems"
                        className="mt-1"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={addCustomStrength}
                        className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Strength
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowAddStrength(false);
                          setNewStrength({ name: '', description: '' });
                        }}
                        className="rounded-full"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Save Button */}
              <div className="mt-6 pt-4 border-t border-slate-100">
                <Button
                  onClick={saveCompanyStrengths}
                  disabled={strengthsLoading}
                  className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full px-6"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {strengthsLoading ? 'Saving...' : 'Save Strengths'}
                </Button>
                <p className="text-xs text-slate-500 mt-2">
                  Active strengths will be used when evaluating residential projects for &ldquo;Alignment with Strengths&rdquo; criteria.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Routines Tab */}
          <TabsContent value="routines" className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading font-semibold text-[#1e3a5f]">
                    Daily Routine Templates
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Configure daily targets and schedules for team routines
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setEditRoutine(null);
                    setShowRoutineModal(true);
                  }}
                  className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Routine
                </Button>
              </div>
              
              {routines.length > 0 ? (
                <div className="space-y-3">
                  {routines.map(routine => (
                    <div 
                      key={routine.id}
                      className={`p-4 rounded-xl border transition-colors ${
                        routine.is_active 
                          ? 'bg-white border-slate-200' 
                          : 'bg-slate-50 border-slate-100 opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${
                            routine.is_active ? 'bg-emerald-500' : 'bg-slate-300'
                          }`} />
                          <div>
                            <p className="font-medium text-slate-800">{routine.title}</p>
                            <p className="text-sm text-slate-500 capitalize">
                              {routine.category?.replace('_', ' ')} • {routine.schedule_type}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1 bg-[#1e3a5f]/10 text-[#1e3a5f] text-sm font-bold rounded-full">
                            Target: {routine.daily_target || 5}
                          </span>
                          <span className="px-3 py-1 bg-slate-100 text-slate-600 text-sm rounded-full">
                            {routine.schedule_time || '09:00'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <Label className="text-xs text-slate-500">Daily Target</Label>
                          <Input
                            type="number"
                            min="1"
                            max="20"
                            value={routine.daily_target || 5}
                            onChange={async (e) => {
                              const newTarget = parseInt(e.target.value) || 5;
                              try {
                                const res = await authFetch(`/admin/routines/${routine.id}`, {
                                  method: 'PUT',
                                  body: { daily_target: newTarget }
                                });
                                if (res.ok) {
                                  loadData();
                                  toast.success('Target updated');
                                }
                              } catch (err) {
                                toast.error('Failed to update');
                              }
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Duration (mins)</Label>
                          <Input
                            type="number"
                            min="5"
                            max="120"
                            value={routine.duration_minutes || 30}
                            onChange={async (e) => {
                              const newDuration = parseInt(e.target.value) || 30;
                              try {
                                const res = await authFetch(`/admin/routines/${routine.id}`, {
                                  method: 'PUT',
                                  body: { duration_minutes: newDuration }
                                });
                                if (res.ok) {
                                  loadData();
                                }
                              } catch (err) {
                                toast.error('Failed to update');
                              }
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Schedule Time</Label>
                          <Input
                            type="time"
                            value={routine.schedule_time || '09:00'}
                            onChange={async (e) => {
                              try {
                                const res = await authFetch(`/admin/routines/${routine.id}`, {
                                  method: 'PUT',
                                  body: { schedule_time: e.target.value }
                                });
                                if (res.ok) {
                                  loadData();
                                }
                              } catch (err) {
                                toast.error('Failed to update');
                              }
                            }}
                            className="mt-1"
                          />
                        </div>
                        <div className="flex items-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const res = await authFetch(`/admin/routines/${routine.id}`, {
                                  method: 'PUT',
                                  body: { is_active: !routine.is_active }
                                });
                                if (res.ok) {
                                  loadData();
                                  toast.success(routine.is_active ? 'Routine disabled' : 'Routine enabled');
                                }
                              } catch (err) {
                                toast.error('Failed to update');
                              }
                            }}
                            className={routine.is_active ? 'text-amber-600' : 'text-emerald-600'}
                          >
                            {routine.is_active ? 'Disable' : 'Enable'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={async () => {
                              if (!confirm('Delete this routine?')) return;
                              try {
                                const res = await authFetch(`/admin/routines/${routine.id}`, {
                                  method: 'DELETE'
                                });
                                if (res.ok) {
                                  loadData();
                                  toast.success('Routine deleted');
                                }
                              } catch (err) {
                                toast.error('Failed to delete');
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Active Days */}
                      <div>
                        <Label className="text-xs text-slate-500 mb-2 block">Active Days</Label>
                        <div className="flex gap-2">
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                            const isActive = (routine.active_days || [0,1,2,3,4]).includes(idx);
                            return (
                              <button
                                key={day}
                                onClick={async () => {
                                  const currentDays = routine.active_days || [0,1,2,3,4];
                                  const newDays = isActive 
                                    ? currentDays.filter(d => d !== idx)
                                    : [...currentDays, idx].sort();
                                  try {
                                    const res = await authFetch(`/admin/routines/${routine.id}`, {
                                      method: 'PUT',
                                      body: { active_days: newDays }
                                    });
                                    if (res.ok) {
                                      loadData();
                                    }
                                  } catch (err) {
                                    toast.error('Failed to update');
                                  }
                                }}
                                className={`w-10 h-10 rounded-lg text-xs font-medium transition-colors ${
                                  isActive 
                                    ? 'bg-[#1e3a5f] text-white' 
                                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                }`}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Quick Actions */}
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-xs text-slate-500">Quick Actions</Label>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs h-7"
                            onClick={async () => {
                              const name = prompt('Enter quick action name:');
                              if (!name?.trim()) return;
                              const script = prompt('Link to script? (leave empty for none):') || null;
                              const current = routine.quick_actions || [];
                              const updated = [...current, { action: name.trim(), script: script?.trim() || null }];
                              try {
                                const res = await authFetch(`/admin/routines/${routine.id}`, {
                                  method: 'PUT',
                                  body: { quick_actions: updated }
                                });
                                if (res.ok) { loadData(); toast.success('Action added'); }
                              } catch { toast.error('Failed'); }
                            }}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Action
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {(routine.quick_actions || []).map((qa, qIdx) => (
                            <div key={qIdx} className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 rounded-full text-xs">
                              <span className="text-slate-700">{qa.action}</span>
                              {qa.script && <span className="text-slate-400 text-[10px]">({qa.script})</span>}
                              <button
                                onClick={async () => {
                                  const updated = (routine.quick_actions || []).filter((_, i) => i !== qIdx);
                                  try {
                                    const res = await authFetch(`/admin/routines/${routine.id}`, {
                                      method: 'PUT',
                                      body: { quick_actions: updated }
                                    });
                                    if (res.ok) { loadData(); toast.success('Action removed'); }
                                  } catch { toast.error('Failed'); }
                                }}
                                className="ml-1 text-slate-400 hover:text-red-500"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          {(!routine.quick_actions || routine.quick_actions.length === 0) && (
                            <span className="text-xs text-slate-400 italic">Using default actions. Add custom ones to override.</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  No routines defined. Click &ldquo;Add Routine&rdquo; to create one.
                </p>
              )}
            </div>
          </TabsContent>

          {/* Email Digest Tab - Enhanced */}
          <TabsContent value="email-digest" className="space-y-6">
            {/* Email Status Alert */}
            {!emailStatus.configured && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-800">Email Not Configured</h4>
                  <p className="text-sm text-red-700 mt-1">{emailStatus.error || 'SMTP credentials are not set. Schedule cannot be enabled.'}</p>
                </div>
              </div>
            )}
            
            {/* Main Settings Card */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="font-heading font-semibold text-[#1e3a5f]">
                    Weekly Email Digest
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Automatically send weekly performance summaries
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${emailDigest.enabled ? 'text-emerald-600 font-medium' : 'text-slate-500'}`}>
                    {emailDigest.enabled ? 'Scheduled' : 'Disabled'}
                  </span>
                  <button
                    onClick={() => setEmailDigest(prev => ({ ...prev, enabled: !prev.enabled }))}
                    disabled={!emailStatus.configured}
                    className={`w-12 h-7 rounded-full transition-all ${
                      emailDigest.enabled ? 'bg-emerald-500' : 'bg-slate-300'
                    } ${!emailStatus.configured ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      emailDigest.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Schedule Configuration */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="space-y-2">
                  <Label>Day of Week</Label>
                  <Select
                    value={emailDigest.day_of_week}
                    onValueChange={(value) => setEmailDigest(prev => ({ ...prev, day_of_week: value }))}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                        <SelectItem key={day} value={day}>
                          {day.charAt(0).toUpperCase() + day.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Hour (Europe/London)</Label>
                  <Select
                    value={String(emailDigest.hour)}
                    onValueChange={(value) => setEmailDigest(prev => ({ ...prev, hour: parseInt(value) }))}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {String(i).padStart(2, '0')}:00
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Minute</Label>
                  <Select
                    value={String(emailDigest.minute)}
                    onValueChange={(value) => setEmailDigest(prev => ({ ...prev, minute: parseInt(value) }))}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 15, 30, 45].map(m => (
                        <SelectItem key={m} value={String(m)}>
                          :{String(m).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Next Run Info */}
              {nextDigestRun && emailDigest.enabled && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm text-emerald-700">
                      <strong>Next scheduled send:</strong> {new Date(nextDigestRun).toLocaleString('en-GB', { 
                        weekday: 'long', 
                        day: 'numeric', 
                        month: 'short', 
                        hour: '2-digit', 
                        minute: '2-digit',
                        timeZoneName: 'short'
                      })}
                    </span>
                  </div>
                </div>
              )}

              {/* Recipients */}
              <div className="mb-6">
                <Label className="mb-3 block">Additional Recipients</Label>
                <p className="text-xs text-slate-500 mb-3">Directors always receive the digest. Select additional team members below.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-1">
                  {eligibleRecipients.map(recipient => (
                    <div 
                      key={recipient.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
                        recipient.is_director 
                          ? 'bg-slate-50 border-slate-200 opacity-60' 
                          : emailDigest.recipients?.includes(recipient.id)
                            ? 'bg-emerald-50 border-emerald-300'
                            : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => !recipient.is_director && toggleRecipient(recipient.id)}
                    >
                      <Checkbox 
                        checked={recipient.is_director || emailDigest.recipients?.includes(recipient.id)}
                        disabled={recipient.is_director}
                        className={recipient.is_director ? 'opacity-50' : ''}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm text-slate-700 truncate">
                          {recipient.name || recipient.email}
                        </div>
                        <div className="text-xs text-slate-500">
                          {recipient.is_director ? 'Director (always receives)' : ROLE_LABELS[recipient.role] || recipient.role}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* What's Included */}
              <div className="p-4 bg-sky-50 rounded-xl border border-sky-200 mb-6">
                <h4 className="font-semibold text-sky-800 mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  What&apos;s Included
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-sky-700">
                  <div>• Outreach sent & replies received</div>
                  <div>• Calls booked</div>
                  <div>• Enquiries triaged</div>
                  <div>• Quotes issued</div>
                  <div>• Won & Lost counts</div>
                  <div>• Paused enquiries</div>
                  <div>• Awaiting approval count</div>
                  <div>• Top 5 stuck items with deep links</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-4 border-t border-slate-100">
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={sendTestDigest}
                    variant="outline"
                    disabled={digestLoading || !emailStatus.configured}
                    className="rounded-xl text-sm"
                    data-testid="send-test-digest-btn"
                  >
                    {digestLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Test
                  </Button>
                  <Button
                    onClick={sendDigestNow}
                    variant="outline"
                    disabled={digestLoading || !emailStatus.configured}
                    className="rounded-xl text-sm"
                    data-testid="send-digest-now-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Now
                  </Button>
                  <Button
                    onClick={loadDigestPreview}
                    variant="outline"
                    className="rounded-xl text-sm"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                </div>
                <Button
                  onClick={saveEmailDigest}
                  disabled={digestLoading}
                  className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl"
                  data-testid="save-digest-settings-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Schedule
                </Button>
              </div>
            </div>

            {/* Send History */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-[#1e3a5f]">
                  Send History
                </h3>
                {digestHistory.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-xl text-xs text-red-600 border-red-200 hover:bg-red-50"
                    onClick={async () => {
                      try {
                        const res = await authFetch('/admin/digest-history/clear', { method: 'DELETE' });
                        if (res.ok) {
                          const data = await res.json();
                          toast.success(`Cleared ${data.deleted} log(s)`);
                          setDigestHistory([]);
                        }
                      } catch { toast.error('Failed to clear'); }
                    }}
                    data-testid="clear-digest-history"
                  >
                    Clear History
                  </Button>
                )}
              </div>
              {digestHistory.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Mail className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No digest emails sent yet</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {digestHistory.map((log, idx) => (
                    <div 
                      key={log.id || idx}
                      className={`flex items-center justify-between p-3 rounded-xl ${
                        log.success ? 'bg-emerald-50' : 'bg-red-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {log.success ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                        <div>
                          <div className="font-medium text-sm text-slate-700">
                            {log.triggered_by === 'scheduler' ? 'Scheduled' : 
                             log.triggered_by === 'test' ? 'Test' : 'Manual'} Send
                          </div>
                          <div className="text-xs text-slate-500">
                            {new Date(log.completed_at).toLocaleString('en-GB')}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-medium ${log.success ? 'text-emerald-700' : 'text-red-700'}`}>
                          {log.success ? `${log.sent_count} sent` : 'Failed'}
                        </div>
                        {log.error_message && (
                          <div className="text-xs text-red-600 max-w-[150px] truncate">
                            {log.error_message}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Digest Preview Modal */}
          <Dialog open={showDigestPreview} onOpenChange={setShowDigestPreview}>
            <DialogContent className="max-w-4xl max-h-[90dvh] p-0 overflow-hidden">
              <DialogHeader className="p-4 border-b">
                <DialogTitle>Digest Email Preview</DialogTitle>
              </DialogHeader>
              <div className="overflow-y-auto max-h-[70vh]">
                <iframe
                  srcDoc={digestPreviewHtml}
                  title="Digest Preview"
                  className="w-full h-[600px] border-0"
                />
              </div>
            </DialogContent>
          </Dialog>

          {/* Export Tab */}
          <TabsContent value="export" className="space-y-6">
            {/* Full Data Backup */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading font-semibold text-[#1e3a5f]">
                    Data Backup & Export
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Download your team&apos;s data for backup or migration
                  </p>
                </div>
                <Badge variant="outline" className="text-slate-500">
                  Director Only
                </Badge>
              </div>

              {/* Full Export Button */}
              <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div className="text-white">
                    <h4 className="font-semibold text-lg mb-1">Complete Data Export</h4>
                    <p className="text-white/70 text-sm">Download all team data as a JSON file including enquiries, quotes, contacts, and more</p>
                  </div>
                  <Button
                    onClick={exportAllData}
                    disabled={exportLoading === 'all'}
                    className="bg-white text-[#1e3a5f] hover:bg-slate-100 rounded-full px-6"
                    data-testid="export-all-btn"
                  >
                    {exportLoading === 'all' ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Database className="w-4 h-4 mr-2" />
                        Export All Data
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Individual CSV Exports */}
              <h4 className="font-medium text-slate-700 mb-4">Export Individual Data (CSV)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { type: 'enquiries', label: 'Enquiries', desc: 'All enquiry records', icon: FileText },
                  { type: 'quotes', label: 'Quotes', desc: 'Quotes and line items', icon: FileDown },
                  { type: 'decisions', label: 'Decisions', desc: 'Decision results', icon: Sliders },
                  { type: 'contacts', label: 'Contacts', desc: 'Pipeline contacts', icon: Users },
                  { type: 'outreach_logs', label: 'Outreach Logs', desc: 'Contact activity', icon: Mail },
                  { type: 'tasks', label: 'Tasks', desc: 'All tasks', icon: CheckCircle2 },
                ].map(item => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.type}
                      onClick={() => exportDataCsv(item.type)}
                      disabled={exportLoading === item.type}
                      className={`p-4 bg-slate-50 rounded-xl text-left hover:bg-slate-100 transition-colors ${
                        exportLoading === item.type ? 'opacity-50' : ''
                      }`}
                      data-testid={`export-csv-${item.type}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <Icon className="w-5 h-5 text-[#1e3a5f]" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{item.label}</p>
                            <p className="text-xs text-slate-500">{item.desc}</p>
                          </div>
                        </div>
                        {exportLoading === item.type ? (
                          <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Legacy JSON exports */}
              <div className="mt-6 pt-6 border-t border-slate-100">
                <h4 className="font-medium text-slate-700 mb-4">Legacy JSON Exports</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    { type: 'enquiries', label: 'Enquiries' },
                    { type: 'quotes', label: 'Quotes' },
                    { type: 'decisions', label: 'Decisions' },
                    { type: 'contacts', label: 'Contacts' },
                    { type: 'scripts', label: 'Scripts' },
                  ].map(item => (
                    <button
                      key={item.type}
                      onClick={() => exportData(item.type)}
                      className="p-3 bg-slate-50 rounded-lg text-center hover:bg-slate-100 transition-colors text-sm"
                      data-testid={`export-json-${item.type}`}
                    >
                      <p className="font-medium text-slate-700">{item.label}</p>
                      <p className="text-xs text-slate-400">JSON</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Info box */}
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Data Export Guidelines</p>
                    <ul className="list-disc list-inside space-y-1 text-amber-700">
                      <li>Exported data contains sensitive business information</li>
                      <li>Store exports securely and in compliance with GDPR</li>
                      <li>Full exports include the last 30 days of action logs</li>
                      <li>User passwords are never included in exports</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Calendar Subscriptions Tab */}
          <TabsContent value="calendar" className="space-y-6">
            {/* Calendar Health Dashboard */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-[#1e3a5f] flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Calendar Health
                </h3>
                <div className="flex items-center gap-2">
                  {calendarHealth && (
                    <Badge 
                      variant={calendarHealth.health_status === 'healthy' ? 'default' : 'destructive'}
                      className={calendarHealth.health_status === 'healthy' ? 'bg-emerald-100 text-emerald-700' : ''}
                    >
                      {calendarHealth.health_status === 'healthy' ? 'Healthy' : 'Needs Attention'}
                    </Badge>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={refreshCalendarHealth}
                    disabled={calendarHealthLoading}
                    className="rounded-xl"
                  >
                    <RefreshCw className={`w-4 h-4 ${calendarHealthLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>

              {!calendarHealth ? (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Loading calendar health data...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 bg-slate-50 rounded-xl text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Users className="w-4 h-4 text-slate-500" />
                      </div>
                      <p className="text-2xl font-bold text-[#1e3a5f]">{calendarHealth.summary?.team_members || 0}</p>
                      <p className="text-xs text-slate-500">Team Members</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Link2 className="w-4 h-4 text-slate-500" />
                      </div>
                      <p className="text-2xl font-bold text-[#1e3a5f]">{calendarHealth.summary?.active_subscriptions || 0}</p>
                      <p className="text-xs text-slate-500">Subscriptions</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Calendar className="w-4 h-4 text-slate-500" />
                      </div>
                      <p className="text-2xl font-bold text-[#1e3a5f]">{calendarHealth.summary?.upcoming_events_7d || 0}</p>
                      <p className="text-xs text-slate-500">Events (7 days)</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-xl text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <AlertTriangle className="w-4 h-4 text-slate-500" />
                      </div>
                      <p className={`text-2xl font-bold ${calendarHealth.summary?.alerts_count > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {calendarHealth.summary?.alerts_count || 0}
                      </p>
                      <p className="text-xs text-slate-500">Alerts</p>
                    </div>
                  </div>

                  {/* Alerts Section */}
                  {calendarHealth.alerts && calendarHealth.alerts.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-600">Alerts</p>
                      {calendarHealth.alerts.map((alert, idx) => (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-xl border flex items-start gap-3 ${
                            alert.type === 'warning' 
                              ? 'bg-amber-50 border-amber-200' 
                              : 'bg-blue-50 border-blue-200'
                          }`}
                        >
                          {alert.type === 'warning' ? (
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          ) : (
                            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          )}
                          <div>
                            <p className={`font-medium ${alert.type === 'warning' ? 'text-amber-800' : 'text-blue-800'}`}>
                              {alert.message}
                            </p>
                            <p className={`text-sm ${alert.type === 'warning' ? 'text-amber-700' : 'text-blue-700'}`}>
                              {alert.detail}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upcoming Events */}
                  {calendarHealth.upcoming_events && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-slate-600">Next 3 Events</p>
                        <div className="flex gap-2 text-xs">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg">
                            {calendarHealth.upcoming_events.by_type?.tasks || 0} tasks
                          </span>
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-lg">
                            {calendarHealth.upcoming_events.by_type?.followups || 0} follow-ups
                          </span>
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-lg hidden md:inline-block">
                            {calendarHealth.upcoming_events.by_type?.quote_deadlines || 0} quotes
                          </span>
                        </div>
                      </div>
                      
                      {calendarHealth.upcoming_events.next_3?.length > 0 ? (
                        <div className="space-y-2">
                          {calendarHealth.upcoming_events.next_3.map((event, idx) => (
                            <div key={idx} className="p-3 bg-slate-50 rounded-xl flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full ${
                                  event.type === 'task' ? 'bg-blue-500' :
                                  event.type === 'followup' ? 'bg-purple-500' :
                                  event.type === 'quote_deadline' ? 'bg-amber-500' :
                                  event.type === 'milestone' ? 'bg-emerald-500' :
                                  'bg-red-500'
                                }`} />
                                <div>
                                  <p className="font-medium text-slate-800 text-sm">{event.title}</p>
                                  <p className="text-xs text-slate-500 capitalize">{event.type?.replace('_', ' ')}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium text-slate-700">
                                  {event.date ? new Date(event.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '-'}
                                </p>
                                {event.time && (
                                  <p className="text-xs text-slate-500">{event.time}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 bg-slate-50 rounded-xl text-center">
                          <p className="text-slate-500 text-sm">No upcoming events in the next 7 days</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Last Synced Info */}
                  {calendarHealth.subscriptions && calendarHealth.subscriptions.length > 0 && (
                    <div className="pt-3 border-t border-slate-100">
                      <p className="text-xs text-slate-500">
                        <Clock className="w-3 h-3 inline-block mr-1" />
                        Last checked: {new Date(calendarHealth.generated_at).toLocaleString('en-GB')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Calendar Settings */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6">
              <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Calendar Settings
              </h3>
              
              <div className="space-y-4">
                {/* Include Details Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <p className="font-medium text-slate-700">Include project details</p>
                    <p className="text-sm text-slate-500">Show client and project names in calendar events</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {calendarSettings?.include_details ? (
                      <ToggleRight className="w-8 h-8 text-emerald-500 cursor-pointer" onClick={async () => {
                        const res = await authFetch('/calendar/settings', { method: 'PUT', body: { include_details: false } });
                        if (res.ok) {
                          setCalendarSettings({ ...calendarSettings, include_details: false });
                          toast.success('Calendar settings updated');
                        }
                      }} />
                    ) : (
                      <ToggleLeft className="w-8 h-8 text-slate-400 cursor-pointer" onClick={async () => {
                        const res = await authFetch('/calendar/settings', { method: 'PUT', body: { include_details: true } });
                        if (res.ok) {
                          setCalendarSettings({ ...calendarSettings, include_details: true });
                          toast.success('Calendar settings updated');
                        }
                      }} />
                    )}
                  </div>
                </div>

                <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-700">
                      When disabled, calendar events show generic titles like &quot;Task due&quot; instead of project-specific information. This protects client confidentiality.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Calendar Subscriptions Dashboard */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                <h3 className="font-heading font-semibold text-[#1e3a5f] flex items-center gap-2">
                  <Link2 className="w-5 h-5" />
                  Calendar Subscriptions
                </h3>
                <div className="flex items-center gap-2">
                  {subscriptionSummary && (
                    <>
                      <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                        {subscriptionSummary.active} active
                      </Badge>
                      {subscriptionSummary.stale > 0 && (
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                          {subscriptionSummary.stale} stale
                        </Badge>
                      )}
                      {subscriptionSummary.revoked > 0 && (
                        <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                          {subscriptionSummary.revoked} revoked
                        </Badge>
                      )}
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowRevokedSubscriptions(!showRevokedSubscriptions)}
                    className={`rounded-xl text-xs ${showRevokedSubscriptions ? 'bg-slate-100' : ''}`}
                  >
                    {showRevokedSubscriptions ? 'Hide Revoked' : 'Show Revoked'}
                  </Button>
                  {subscriptionSummary?.revoked > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-xl text-xs text-red-600 border-red-200 hover:bg-red-50"
                      onClick={async () => {
                        try {
                          const res = await authFetch('/calendar/subscriptions/clear-revoked', { method: 'DELETE' });
                          if (res.ok) {
                            const data = await res.json();
                            toast.success(`Cleared ${data.deleted} revoked subscription(s)`);
                            loadData();
                          }
                        } catch { toast.error('Failed to clear'); }
                      }}
                      data-testid="clear-revoked-subs"
                    >
                      Clear Revoked
                    </Button>
                  )}
                </div>
              </div>

              {calendarSubscriptions.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No calendar subscriptions</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Team members can create subscriptions from the Calendar page
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {calendarSubscriptions
                    .filter(sub => showRevokedSubscriptions || sub.status !== 'revoked')
                    .map((sub, idx) => (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-xl border ${
                        sub.status === 'revoked' 
                          ? 'bg-red-50/50 border-red-200' 
                          : sub.warnings?.includes('stale_30_days')
                            ? 'bg-amber-50/50 border-amber-200'
                            : 'bg-slate-50 border-slate-100'
                      }`}
                      data-testid={`subscription-${sub.user_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            sub.status === 'revoked' 
                              ? 'bg-red-100' 
                              : sub.warnings?.includes('stale_30_days')
                                ? 'bg-amber-100'
                                : 'bg-[#1e3a5f]/10'
                          }`}>
                            <Users className={`w-5 h-5 ${
                              sub.status === 'revoked' 
                                ? 'text-red-500' 
                                : sub.warnings?.includes('stale_30_days')
                                  ? 'text-amber-600'
                                  : 'text-[#1e3a5f]'
                            }`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-800">{sub.user_name || 'Unknown User'}</p>
                              {/* Status Badge */}
                              {sub.status === 'active' && (
                                <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-0">Active</Badge>
                              )}
                              {sub.status === 'revoked' && (
                                <Badge className="text-[10px] bg-red-100 text-red-700 border-0">Revoked</Badge>
                              )}
                              {sub.status === 'unknown' && (
                                <Badge className="text-[10px] bg-slate-100 text-slate-700 border-0">Unknown</Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">{sub.user_email}</p>
                            {/* Warnings */}
                            {sub.warnings && sub.warnings.length > 0 && sub.status !== 'revoked' && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {sub.warnings.includes('never_synced') && (
                                  <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Never synced</span>
                                )}
                                {sub.warnings.includes('stale_30_days') && (
                                  <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">No sync in 30+ days</span>
                                )}
                                {sub.warnings.includes('all_events_disabled') && (
                                  <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">All events disabled</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {sub.status !== 'revoked' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setRevokeTarget(sub);
                              setShowRevokeModal(true);
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                            title="Revoke subscription"
                            data-testid={`revoke-btn-${sub.user_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                        <div className="p-2 bg-white rounded-lg">
                          <p className="text-xs text-slate-500">Token</p>
                          <p className={`text-xs font-medium ${sub.token_status === 'valid' ? 'text-emerald-600' : 'text-red-600'}`}>
                            {sub.token_status === 'valid' ? 'Valid' : 'Revoked'}
                          </p>
                        </div>
                        <div className="p-2 bg-white rounded-lg">
                          <p className="text-xs text-slate-500">Created</p>
                          <p className="text-xs font-medium text-slate-700">
                            {sub.created_at ? new Date(sub.created_at).toLocaleDateString('en-GB') : '-'}
                          </p>
                        </div>
                        <div className="p-2 bg-white rounded-lg">
                          <p className="text-xs text-slate-500">Last Sync</p>
                          <p className={`text-xs font-medium ${
                            !sub.last_accessed ? 'text-amber-600' : 'text-slate-700'
                          }`}>
                            {sub.last_accessed 
                              ? new Date(sub.last_accessed).toLocaleDateString('en-GB') 
                              : 'Never'}
                          </p>
                        </div>
                        <div className="p-2 bg-white rounded-lg">
                          <p className="text-xs text-slate-500">Syncs</p>
                          <p className="text-xs font-medium text-slate-700">{sub.access_count || 0}</p>
                        </div>
                      </div>
                      {sub.status === 'revoked' && sub.revoked_at && (
                        <div className="mt-2 pt-2 border-t border-red-200">
                          <p className="text-xs text-red-600">
                            Revoked on {new Date(sub.revoked_at).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Revoke Confirmation Modal */}
            <Dialog open={showRevokeModal} onOpenChange={setShowRevokeModal}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-red-600 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Revoke Calendar Subscription
                  </DialogTitle>
                  <DialogDescription>
                    This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                {revokeTarget && (
                  <div className="py-4 space-y-3">
                    <div className="p-3 bg-red-50 rounded-xl border border-red-200">
                      <p className="text-sm text-red-800 font-medium mb-2">
                        You are about to revoke calendar access for:
                      </p>
                      <p className="text-sm text-red-700">
                        <strong>{revokeTarget.user_name}</strong> ({revokeTarget.user_email})
                      </p>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                      <p className="text-sm text-amber-800">
                        <strong>Warning:</strong> Their external calendar (Apple, Google, Outlook) 
                        will immediately stop receiving updates. They will need to create a new 
                        subscription from the Calendar page.
                      </p>
                    </div>
                  </div>
                )}
                <DialogFooter className="gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowRevokeModal(false);
                      setRevokeTarget(null);
                    }}
                    className="rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      if (!revokeTarget) return;
                      try {
                        const res = await authFetch(`/calendar/subscriptions/${revokeTarget.user_id}/revoke`, { 
                          method: 'POST' 
                        });
                        if (res.ok) {
                          // Update local state to show revoked status
                          setCalendarSubscriptions(subs => 
                            subs.map(s => s.user_id === revokeTarget.user_id 
                              ? { ...s, status: 'revoked', token_status: 'revoked', revoked_at: new Date().toISOString() }
                              : s
                            )
                          );
                          // Update summary counts
                          if (subscriptionSummary) {
                            setSubscriptionSummary({
                              ...subscriptionSummary,
                              active: subscriptionSummary.active - 1,
                              revoked: subscriptionSummary.revoked + 1
                            });
                          }
                          toast.success(`Calendar subscription for ${revokeTarget.user_name} has been revoked`);
                        } else {
                          const err = await res.json();
                          toast.error(err.detail || 'Failed to revoke subscription');
                        }
                      } catch (err) {
                        toast.error('Failed to revoke subscription');
                      }
                      setShowRevokeModal(false);
                      setRevokeTarget(null);
                    }}
                    className="rounded-xl bg-red-600 hover:bg-red-700"
                    data-testid="confirm-revoke-btn"
                  >
                    Revoke Access
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Security Info */}
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-800">
                  <p className="font-medium mb-1">Calendar Security</p>
                  <ul className="list-disc list-inside space-y-1 text-emerald-700">
                    <li>One-way export only - OpsFlow never reads external calendars</li>
                    <li>Subscription URLs are private and unique per user</li>
                    <li>Tokens can be revoked immediately if compromised</li>
                    <li>No OAuth integration for calendar read access</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* System Tab - Build Info & SMTP Configuration */}
          <TabsContent value="system" className="space-y-6">
            {/* Build Info Panel */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6">
              <h3 className="font-heading font-semibold text-[#1e3a5f] mb-4">
                Build Information
              </h3>
              {buildInfo ? (
                <div className="space-y-3">
                  {/* Database Connection Status - CRITICAL */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-slate-600 font-medium">Database Status</span>
                    <div className="flex items-center gap-2">
                      {buildInfo.db_connected ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm font-medium text-emerald-600">Connected</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 text-red-500" />
                          <span className="text-sm font-medium text-red-600">Disconnected</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-slate-600">Database Host</span>
                    <code className={`font-mono text-sm ${buildInfo.db_connected ? 'text-emerald-700' : 'text-red-700'}`}>
                      {buildInfo.db_host || 'Not configured'}
                    </code>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-slate-600">Database Name</span>
                    <code className="font-mono text-sm text-slate-700">
                      {buildInfo.db_name || 'N/A'}
                    </code>
                  </div>
                  {buildInfo.db_error && (
                    <div className="p-3 bg-red-50 rounded-xl border border-red-100">
                      <span className="text-sm text-red-700 font-mono">{buildInfo.db_error}</span>
                    </div>
                  )}
                  
                  {/* Scheduler Status */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-slate-600 font-medium">Scheduler Status</span>
                    <div className="flex items-center gap-2">
                      {buildInfo.scheduler_status === 'running' ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm font-medium text-emerald-600">Running</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 text-amber-500" />
                          <span className="text-sm font-medium text-amber-600">
                            {buildInfo.scheduler_status || 'Disabled'}
                            {buildInfo.scheduler_reason && ` (${buildInfo.scheduler_reason})`}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Services */}
                  {buildInfo.services && (
                    <div className="p-3 bg-slate-50 rounded-xl space-y-2">
                      <span className="text-slate-600 font-medium text-sm">Services</span>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(buildInfo.services).map(([service, enabled]) => (
                          <div key={service} className="flex items-center gap-2">
                            {enabled ? (
                              <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                            ) : (
                              <XCircle className="w-3 h-3 text-slate-400" />
                            )}
                            <span className="text-xs text-slate-600">{service.replace(/_/g, ' ')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="border-t border-slate-200 pt-3 mt-3">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                      <span className="text-slate-600">Build ID</span>
                      <code className="font-mono text-sm text-slate-700">
                        {process.env.REACT_APP_BUILD_ID || 'dev'}
                      </code>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-slate-600">Build Time</span>
                    <span className="font-mono text-sm">
                      {process.env.REACT_APP_BUILD_TIME || new Date().toISOString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-slate-600">App URL</span>
                    <span className={`font-medium ${buildInfo.app_url_configured ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {buildInfo.app_url !== 'NOT SET' ? buildInfo.app_url : 'Using default'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-slate-600">Environment</span>
                    <span className="font-mono text-sm">{buildInfo.environment}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-slate-600">SMTP Configured</span>
                    <div className="flex items-center gap-2">
                      {buildInfo.smtp_configured ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-amber-500" />
                      )}
                      <span className={`font-medium ${buildInfo.smtp_configured ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {buildInfo.smtp_configured ? `Yes (${buildInfo.smtp_host})` : 'Not Configured'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-slate-600">Resend Fallback</span>
                    <span className={`font-medium ${buildInfo.resend_configured ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {buildInfo.resend_configured ? 'Available' : 'Not Set'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-slate-600">Server Time</span>
                    <span className="font-mono text-sm">{buildInfo.server_time}</span>
                  </div>
                  {buildInfo.counts && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">Real-Time Counts</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { label: 'Active Users', value: buildInfo.counts.active_users, total: buildInfo.counts.total_users },
                          { label: 'Contacts', value: buildInfo.counts.contacts },
                          { label: 'Enquiries', value: buildInfo.counts.enquiries },
                          { label: 'Quotes', value: buildInfo.counts.quotes },
                          { label: 'Outreach', value: buildInfo.counts.outreach },
                          { label: 'Active Sequences', value: buildInfo.counts.active_sequences },
                        ].map((c) => (
                          <div key={c.label} className="p-3 bg-white border border-slate-200 rounded-xl text-center">
                            <p className="text-lg font-bold text-[#1e3a5f]">{c.value}</p>
                            <p className="text-[10px] text-slate-500">{c.label}{c.total ? ` (${c.total} total)` : ''}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">Loading build info...</div>
              )}
            </div>

            {/* SMTP Configuration */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading font-semibold text-[#1e3a5f]">
                    SMTP Email Configuration
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Configure SMTP for digest emails, quote sending, and follow-up reminders
                  </p>
                </div>
                {smtpConfigured && smtpTestPassed && (
                  <div className="flex items-center gap-2 text-emerald-600 text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    Configured & Tested
                  </div>
                )}
                {smtpConfigured && !smtpTestPassed && (
                  <div className="flex items-center gap-2 text-amber-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    Configured (test required)
                  </div>
                )}
              </div>

              {/* Microsoft 365 Setup Guide */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-6">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <strong>Microsoft 365 / Outlook Setup:</strong>
                    <ul className="mt-2 space-y-1 text-blue-700">
                      <li>• Host: <code className="bg-blue-100 px-1 rounded">smtp.office365.com</code></li>
                      <li>• Port: <code className="bg-blue-100 px-1 rounded">587</code> with STARTTLS</li>
                      <li>• Use an <strong>app-specific password</strong>, not your primary account password</li>
                      <li>• Use a <strong>dedicated mailbox</strong> (e.g., notifications@yourdomain.com)</li>
                      <li>• Ensure <strong>SMTP AUTH</strong> is enabled in Microsoft 365 Admin Center:<br/>
                        <span className="text-xs">Admin → Users → Active Users → Select User → Mail → Manage Email Apps → Enable &quot;Authenticated SMTP&quot;</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="space-y-2">
                  <Label>SMTP Host *</Label>
                  <Input
                    value={smtpConfig.smtp_host}
                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_host: e.target.value }))}
                    placeholder="smtp.office365.com"
                    className="rounded-xl"
                    data-testid="smtp-host-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>SMTP Port</Label>
                  <Input
                    type="number"
                    value={smtpConfig.smtp_port}
                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                    className="rounded-xl"
                    data-testid="smtp-port-input"
                  />
                  <p className="text-xs text-slate-500">587 for STARTTLS, 465 for SSL</p>
                </div>
                <div className="space-y-2">
                  <Label>Username (Email) *</Label>
                  <Input
                    value={smtpConfig.smtp_username}
                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_username: e.target.value }))}
                    placeholder="notifications@yourcompany.com"
                    className="rounded-xl"
                    data-testid="smtp-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>App Password {smtpConfigured ? '(leave blank to keep existing)' : '*'}</Label>
                  <Input
                    type="password"
                    value={smtpConfig.smtp_password}
                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_password: e.target.value }))}
                    placeholder={smtpConfigured ? '••••••••' : 'Enter app-specific password'}
                    className="rounded-xl"
                    autoComplete="new-password"
                    data-testid="smtp-password-input"
                  />
                  <p className="text-xs text-slate-500">Use app password, not primary account password</p>
                </div>
                <div className="space-y-2">
                  <Label>Sender Email *</Label>
                  <Input
                    value={smtpConfig.sender_email}
                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, sender_email: e.target.value }))}
                    placeholder="notifications@yourcompany.com"
                    className="rounded-xl"
                    data-testid="smtp-sender-email-input"
                  />
                  <p className="text-xs text-slate-500">Usually same as username</p>
                </div>
                <div className="space-y-2">
                  <Label>Sender Name</Label>
                  <Input
                    value={smtpConfig.sender_name}
                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, sender_name: e.target.value }))}
                    placeholder="FURD Tech OpsFlow"
                    className="rounded-xl"
                    data-testid="smtp-sender-name-input"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Reply-To Email</Label>
                  <Input
                    value={smtpConfig.reply_to_email || ''}
                    onChange={(e) => setSmtpConfig(prev => ({ ...prev, reply_to_email: e.target.value }))}
                    placeholder="Same as sender email if left blank"
                    className="rounded-xl"
                    data-testid="smtp-reply-to-input"
                  />
                  <p className="text-xs text-slate-500">Where client replies will be sent</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={smtpConfig.smtp_use_tls}
                    onCheckedChange={(checked) => setSmtpConfig(prev => ({ ...prev, smtp_use_tls: checked, smtp_use_ssl: checked ? false : prev.smtp_use_ssl }))}
                    data-testid="smtp-starttls-checkbox"
                  />
                  <span className="text-sm text-slate-700">Use STARTTLS (recommended for port 587)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={smtpConfig.smtp_use_ssl}
                    onCheckedChange={(checked) => setSmtpConfig(prev => ({ ...prev, smtp_use_ssl: checked, smtp_use_tls: checked ? false : prev.smtp_use_tls }))}
                    data-testid="smtp-ssl-checkbox"
                  />
                  <span className="text-sm text-slate-700">Use SSL/TLS (for port 465)</span>
                </label>
              </div>

              {/* Security Notice */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl mb-6">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-emerald-800">
                    <strong>Security:</strong> Passwords are encrypted with AES-256 before storage and are never logged, 
                    echoed, or exposed via API. Only masked indicators (••••••••) are shown after saving.
                  </div>
                </div>
              </div>

              {/* Test Email Section */}
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-slate-800">Send Test Email</h4>
                    <p className="text-xs text-slate-500">Verify delivery before enabling email features</p>
                  </div>
                  {smtpTestPassed && (
                    <Badge className="bg-emerald-500 text-white">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Test Passed
                    </Badge>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="your-email@example.com"
                    className="rounded-xl flex-1"
                    data-testid="smtp-test-email-input"
                  />
                  <Button
                    onClick={sendTestEmail}
                    variant="outline"
                    disabled={smtpTesting || !smtpConfigured || !testEmailAddress}
                    className="rounded-xl whitespace-nowrap"
                    data-testid="smtp-send-test-btn"
                  >
                    {smtpTesting ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Test Email
                      </>
                    )}
                  </Button>
                </div>
                {!smtpConfigured && (
                  <p className="text-xs text-amber-600 mt-2">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    Save SMTP configuration first before testing
                  </p>
                )}
                {smtpTestResult && (
                  <div className={`mt-3 p-3 rounded-lg text-sm ${smtpTestResult.success ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                    {smtpTestResult.success ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Test email sent successfully! Check your inbox.</span>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <XCircle className="w-4 h-4" />
                          <span className="font-medium">Test failed</span>
                        </div>
                        <p className="text-xs">{smtpTestResult.error}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Email Features Warning */}
              {!smtpTestPassed && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <strong>Note:</strong> Email features (quote sending, follow-up reminders, weekly digest) 
                      are blocked until SMTP is configured and tested successfully.
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-100">
                <Button
                  onClick={testSmtpConnection}
                  variant="outline"
                  disabled={smtpTesting || !smtpConfig.smtp_host}
                  className="rounded-xl"
                  data-testid="smtp-test-connection-btn"
                >
                  {smtpTesting ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Wifi className="w-4 h-4 mr-2" />
                  )}
                  Test Connection
                </Button>
                <Button
                  onClick={saveSmtpConfig}
                  className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl"
                  data-testid="smtp-save-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save SMTP Configuration
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Invite Team Member Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1e3a5f]">
              Invite Team Member
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={inviteTeamMember} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={inviteData.first_name}
                  onChange={(e) => setInviteData(prev => ({ ...prev, first_name: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={inviteData.last_name}
                  onChange={(e) => setInviteData(prev => ({ ...prev, last_name: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={inviteData.email}
                onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={inviteData.role}
                onValueChange={(value) => setInviteData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="director">Director</SelectItem>
                  <SelectItem value="bd_manager">Business Development Manager</SelectItem>
                  <SelectItem value="senior_engineer">Senior Engineer</SelectItem>
                  <SelectItem value="associate_engineer">Associate Engineer</SelectItem>
                  <SelectItem value="engineer">Engineer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowInviteModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#162c46] text-white">
                Send Invite
              </Button>
            </div>
          </form>
          <p className="text-xs text-slate-500 mt-2">
            An invite email will be sent to the user with a link to create their account.
          </p>
        </DialogContent>
      </Dialog>

      {/* Edit Team Member Modal */}
      <Dialog open={showEditMemberModal} onOpenChange={setShowEditMemberModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>
              Update the name for {editMemberData?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editFirstName">First Name</Label>
              <Input
                id="editFirstName"
                value={editMemberData?.first_name || ''}
                onChange={(e) => setEditMemberData(prev => ({ ...prev, first_name: e.target.value }))}
                placeholder="Enter first name"
                data-testid="edit-member-first-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editLastName">Last Name</Label>
              <Input
                id="editLastName"
                value={editMemberData?.last_name || ''}
                onChange={(e) => setEditMemberData(prev => ({ ...prev, last_name: e.target.value }))}
                placeholder="Enter last name"
                data-testid="edit-member-last-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditMemberModal(false);
                setEditMemberData(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={saveEditMember}
              disabled={editMemberSaving}
              className="bg-[#1e3a5f] hover:bg-[#162c46]"
              data-testid="save-edit-member-btn"
            >
              {editMemberSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};
